import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, Stack,
  CircularProgress, Alert, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem,
} from '@mui/material';
import { ArrowBack, Cancel, Edit, DriveFileMove } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentService, projectService, farmOperationService } from '@smartgrow/shared';
import type { PaymentStatus } from '@smartgrow/shared';
import { useState } from 'react';

const STATUS_COLORS: Record<PaymentStatus, 'default' | 'warning' | 'success' | 'error'> = {
  PENDING: 'warning',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
};

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;

const Row = ({ label, value, onClick }: { label: string; value?: string | number | null; onClick?: () => void }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography
      variant="body2" fontWeight="medium"
      color={onClick ? 'primary' : undefined}
      sx={{ textAlign: 'right', maxWidth: '65%', ...(onClick && { cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }) }}
      onClick={onClick}
    >
      {value ?? '—'}
    </Typography>
  </Box>
);

export const PaymentDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const paymentId = id!;
  const [actionError, setActionError] = useState('');
  const [editOpen, setEditOpen] = useState(false);
  const [editAmount, setEditAmount] = useState('');
  const [editReason, setEditReason] = useState('');
  const [moveOpen, setMoveOpen] = useState(false);
  const [moveProjectId, setMoveProjectId] = useState('');
  const [moveOperationId, setMoveOperationId] = useState('');

  const userData = JSON.parse(localStorage.getItem('user_data') ?? '{}');
  const isAdminOrOwner = userData?.role === 'ADMIN' || userData?.role === 'OWNER';

  const { data: payment, isLoading, isError } = useQuery({
    queryKey: ['payment', paymentId],
    queryFn: () => paymentService.getPaymentById(paymentId),
    enabled: !!paymentId,
  });

  const cancelMutation = useMutation({
    mutationFn: () => paymentService.cancelPayment(paymentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment', paymentId] });
      qc.invalidateQueries({ queryKey: ['payments'] });
      // Cancelling changes the same aggregate totals an amount correction does — same
      // invalidation set as updateAmountMutation below.
      qc.invalidateQueries({ queryKey: ['payments-by-operation'] });
      qc.invalidateQueries({ queryKey: ['farm-financial-summary'] });
      if (payment?.expenseRequestId) {
        qc.invalidateQueries({ queryKey: ['expense', payment.expenseRequestId] });
        qc.invalidateQueries({ queryKey: ['expenses'] });
      }
      setActionError('');
    },
    onError: () => setActionError('Failed to cancel payment.'),
  });

  const updateAmountMutation = useMutation({
    mutationFn: () => paymentService.updatePaymentAmount(paymentId, {
      amount: Number(editAmount),
      reason: editReason.trim() || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment', paymentId] });
      qc.invalidateQueries({ queryKey: ['payments'] });
      qc.invalidateQueries({ queryKey: ['payments-by-operation'] });
      qc.invalidateQueries({ queryKey: ['farm-financial-summary'] });
      // amountPaid on the linked expense request (if any) is derived live from this payment —
      // stale until its own query is invalidated too.
      if (payment?.expenseRequestId) {
        qc.invalidateQueries({ queryKey: ['expense', payment.expenseRequestId] });
        qc.invalidateQueries({ queryKey: ['expenses'] });
      }
      setActionError('');
      setEditOpen(false);
      setEditReason('');
    },
    onError: () => setActionError('Failed to update payment amount.'),
  });

  const openEditDialog = () => {
    setEditAmount(String(payment?.amount ?? ''));
    setEditReason('');
    setEditOpen(true);
  };

  const { data: projectsForMove } = useQuery({
    queryKey: ['projects-for-filter'],
    queryFn: () => projectService.getProjects({ size: 100 }),
    enabled: moveOpen,
  });

  const { data: operationsForMove } = useQuery({
    queryKey: ['operations-for-filter', moveProjectId],
    queryFn: () => farmOperationService.getByProject(moveProjectId),
    enabled: moveOpen && !!moveProjectId,
  });

  const moveMutation = useMutation({
    mutationFn: () => moveOperationId
      ? paymentService.moveToOperation(paymentId, moveOperationId)
      : paymentService.moveToProject(paymentId, moveProjectId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment', paymentId] });
      qc.invalidateQueries({ queryKey: ['payments'] });
      setActionError('');
      setMoveOpen(false);
      setMoveProjectId('');
      setMoveOperationId('');
    },
    onError: () => setActionError('Failed to move payment. It may be linked to an expense request or contract — move that instead.'),
  });

  const openMoveDialog = () => {
    setMoveProjectId('');
    setMoveOperationId('');
    setMoveOpen(true);
  };

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  if (isError || !payment) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load payment.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  const title = `${payment.paymentType?.replace(/_/g, ' ')}${payment.contractorName ? ` — ${payment.contractorName}` : ''}`;

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">{title}</Typography>
          {payment.referenceNumber && (
            <Typography variant="caption" color="text.secondary" fontFamily="monospace">{payment.referenceNumber}</Typography>
          )}
        </Box>
        <Chip label={payment.status} color={STATUS_COLORS[payment.status]} size="small" />
      </Stack>

      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}

      {isAdminOrOwner && payment.status !== 'CANCELLED' && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Edit />}
            onClick={openEditDialog}
          >
            Edit Amount
          </Button>
          {!payment.expenseRequestNumber && (
            <Button
              variant="outlined"
              startIcon={<DriveFileMove />}
              onClick={openMoveDialog}
            >
              Move
            </Button>
          )}
          <Button
            variant="outlined"
            color="error"
            startIcon={cancelMutation.isPending ? <CircularProgress size={16} /> : <Cancel />}
            disabled={cancelMutation.isPending}
            onClick={() => cancelMutation.mutate()}
          >
            Cancel Payment
          </Button>
        </Stack>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Payment Details</Typography>
              <Divider sx={{ mb: 1 }} />
              <Row label="Amount" value={fmt(payment.amount)} />
              <Row label="Payment Type" value={payment.paymentType?.replace(/_/g, ' ')} />
              <Row label="Payment Date" value={payment.paymentDate} />
              <Row label="Payment Method" value={payment.paymentMethod?.replace(/_/g, ' ')} />
              <Row label="Reference Number" value={payment.referenceNumber} />
              <Row label="Paid By" value={payment.paidByName} />
              {payment.notes && <Row label="Notes" value={payment.notes} />}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Linked To</Typography>
              <Divider sx={{ mb: 1 }} />
              {payment.expenseRequestNumber && <Row label="Expense Request" value={payment.expenseRequestNumber} />}
              {payment.recipientUserName && <Row label="Recipient" value={payment.recipientUserName} />}
              {(payment.periodMonth && payment.periodYear) && (
                <Row label="Salary Period" value={`${payment.periodMonth}/${payment.periodYear}`} />
              )}
              {payment.contractorName && <Row label="Contractor" value={payment.contractorName} />}
              {payment.contractorContact && <Row label="Contractor Contact" value={payment.contractorContact} />}
              {payment.contractorDescription && <Row label="Description" value={payment.contractorDescription} />}
              {payment.procurementItem && <Row label="Procurement Item" value={payment.procurementItemCustom || payment.procurementItem} />}
              {payment.supplierName && <Row label="Supplier" value={payment.supplierName} />}
              {payment.description && <Row label="Description" value={payment.description} />}
              {payment.projectName && (
                <Row label="Project" value={payment.projectName}
                  onClick={payment.projectId ? () => navigate(`/dashboard/projects/${payment.projectId}`) : undefined} />
              )}
              {payment.farmOperationName && (
                <Row label="Farm Operation" value={payment.farmOperationName}
                  onClick={payment.farmOperationId ? () => navigate(`/dashboard/farm/${payment.farmOperationId}`) : undefined} />
              )}
              {payment.operationTaskTitle && <Row label="Task" value={payment.operationTaskTitle} />}
              {!payment.expenseRequestNumber && !payment.recipientUserName && !payment.contractorName
                && !payment.procurementItem && !payment.farmOperationName && !payment.projectName
                && !payment.description && (
                <Typography variant="body2" color="text.secondary">Not linked to any record.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Correct Payment Amount</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Current amount: {fmt(payment.amount)}
          </Typography>
          <TextField
            label="Corrected Amount"
            type="number"
            fullWidth
            autoFocus
            value={editAmount}
            onChange={(e) => setEditAmount(e.target.value)}
            sx={{ mb: 2 }}
            inputProps={{ min: 0.01, step: 0.01 }}
          />
          <TextField
            label="Reason (optional)"
            fullWidth
            multiline
            minRows={2}
            value={editReason}
            onChange={(e) => setEditReason(e.target.value)}
            placeholder="e.g. amount was mistyped at time of payment"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={updateAmountMutation.isPending || !editAmount || Number(editAmount) <= 0}
            onClick={() => updateAmountMutation.mutate()}
          >
            {updateAmountMutation.isPending ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={moveOpen} onClose={() => setMoveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Move Payment</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Move this payment to a different project, or to a specific farm operation within it.
          </Typography>
          <TextField select fullWidth label="Target Project" value={moveProjectId} sx={{ mb: 2 }}
            onChange={(e) => { setMoveProjectId(e.target.value); setMoveOperationId(''); }}>
            {(projectsForMove?.content ?? []).map((p) => (
              <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>
            ))}
          </TextField>
          <TextField select fullWidth label="Farm Operation (optional)" value={moveOperationId} disabled={!moveProjectId}
            helperText={!moveProjectId ? 'Select a project first' : 'Leave blank to move to the project directly'}
            onChange={(e) => setMoveOperationId(e.target.value)}>
            <MenuItem value="">— None —</MenuItem>
            {(operationsForMove?.content ?? []).map((op) => (
              <MenuItem key={op.id} value={op.id}>{op.name}</MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMoveOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!moveProjectId || moveMutation.isPending}
            onClick={() => moveMutation.mutate()}
          >
            {moveMutation.isPending ? <CircularProgress size={20} /> : 'Move'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
